import React, { FunctionComponent, useMemo, useCallback } from 'react';
import { Accordion, AccordionSummary, AccordionDetails } from '@rbx/ui';
import { OpenAPI } from '@rbx/docsite-utilities/dist/browser';
import { getEndpointRiskLevel } from '@modules/openCloud/swagger/config/endpointRiskLevels';
import {
  convertRobloxUrlToCurrentEnvironment,
  getBedev2BaseUrl,
} from '@modules/utilities/constants/baseUrl';
import DocsSiteEventType from '@modules/eventStream/enum/DocsSiteEventType';
import DocsSiteSource from '@modules/eventStream/enum/DocsSiteSource';
import unifiedLoggerClient from '@modules/eventStream/implementations/unifiedLoggerClient';
import OperationSummary from './OperationSummary';
import OperationDetails from './OperationDetails';
import useOperationStyles from './Operation.styles';
import useOperationExpansion from '../hooks/useOperationExpansion';
import { useOpenAPIDocument } from '../hooks/useOpenAPIDocument';
import TryItOutProvider from '../context/TryItOutProvider';
import OperationProvider from '../context/OperationProvider';
import { getFirstBaseUrl } from '../utils/serverUtils';

/**
 * Calculates operation metadata for a given operation
 */
const calculateOperationMetadata = (
  operation: OpenAPI.OperationObject,
  document: OpenAPI.Document | null,
  method: OpenAPI.HttpMethod,
  path: string,
) => {
  const serverUrl = document ? getFirstBaseUrl(operation, document) || '' : '';
  const operationId =
    operation.operationId || `${method.toLowerCase()}${path.replace(/[^a-zA-Z0-9]/g, '')}`;

  const hasSupportedAuthTypes =
    !convertRobloxUrlToCurrentEnvironment(serverUrl).includes(getBedev2BaseUrl());
  const riskLevel = getEndpointRiskLevel(method.toUpperCase(), serverUrl, path);
  const url = `${serverUrl}${path}`;

  return {
    operationId,
    serverUrl,
    hasSupportedAuthTypes,
    riskLevel,
    url,
  };
};

type OperationProps = {
  operation: OpenAPI.OperationObject;
  method: OpenAPI.HttpMethod;
  path: string;
};

const Operation: FunctionComponent<OperationProps> = ({ operation, method, path }) => {
  const { classes } = useOperationStyles();
  const { isExpanded, handleToggle: originalHandleToggle } = useOperationExpansion(
    operation,
    method,
    path,
  );
  const document = useOpenAPIDocument();

  // Calculate operation metadata
  const operationMetadata = useMemo(
    () => calculateOperationMetadata(operation, document, method, path),
    [document, operation, method, path],
  );

  const handleToggle = useCallback(
    (e: React.MouseEvent) => {
      const newExpanded = !isExpanded;

      unifiedLoggerClient.logClickEvent({
        eventName: newExpanded
          ? DocsSiteEventType.OpenCloudEndpointAccordionOpened
          : DocsSiteEventType.OpenCloudEndpointAccordionClosed,
        parameters: {
          source: DocsSiteSource.OpenCloud,
          method: method.toUpperCase(),
          url: operationMetadata.url,
          riskLevel: String(operationMetadata.riskLevel),
          operationId: operationMetadata.operationId,
        },
      });
      originalHandleToggle(e);
    },
    [isExpanded, originalHandleToggle, method, operationMetadata],
  );

  return (
    <OperationProvider
      operationId={operationMetadata.operationId}
      method={method}
      path={path}
      url={operationMetadata.url}
      riskLevel={operationMetadata.riskLevel}
      hasSupportedAuthTypes={operationMetadata.hasSupportedAuthTypes}>
      <TryItOutProvider>
        <Accordion
          classes={{ root: classes.accordion }}
          expanded={isExpanded}
          slotProps={{ transition: { timeout: 200, unmountOnExit: true } }}>
          <AccordionSummary classes={{ root: classes.accordionSummary }} onClick={handleToggle}>
            <OperationSummary operation={operation} method={method} path={path} />
          </AccordionSummary>
          <AccordionDetails classes={{ root: classes.accordionDetails }}>
            <OperationDetails operation={operation} method={method} path={path} />
          </AccordionDetails>
        </Accordion>
      </TryItOutProvider>
    </OperationProvider>
  );
};

export default Operation;
